import emailjs from "@emailjs/browser";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ContactModal = ({ isOpen, onClose }: ContactModalProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    projectType: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setSending(true);
    setError(false);

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
        {
          from_name: formData.name,
          from_email: formData.email,
          company: formData.company,
          project_type: formData.projectType,
          message: formData.message,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!
      );
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSubmitted(false);
      setError(false);
      setFormData({ name: "", company: "", email: "", projectType: "", message: "" });
    }, 300);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl border border-gray-700 bg-gradient-to-b from-gray-900 to-gray-950 shadow-2xl shadow-green-indie/10"
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 40 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Green glow accent at top */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px bg-gradient-to-r from-transparent via-green-indie to-transparent" />

            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors z-10 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            <AnimatePresence mode="wait">
              {!submitted ? (
                <motion.form
                  key="form"
                  onSubmit={handleSubmit}
                  className="p-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                >
                  {/* Header */}
                  <div className="text-center mb-6">
                    <motion.div
                      className="inline-flex items-center gap-2 bg-green-indie/10 border border-green-indie/20 rounded-full px-4 py-1.5 mb-4"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <div className="w-2 h-2 rounded-full bg-green-indie animate-pulse" />
                      <span className="text-green-indie text-xs font-semibold uppercase tracking-wider">
                        {t("modal.available")}
                      </span>
                    </motion.div>
                    <h2 className="text-2xl font-bold text-white">
                      {t("modal.title")}
                    </h2>
                    <p className="text-gray-500 text-sm mt-1">
                      {t("modal.subtitle")}
                    </p>
                  </div>

                  {/* Name + Company row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-400 mb-1.5 text-xs font-semibold uppercase tracking-wider">
                        {t("modal.name")}
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                        className="w-full px-4 py-2.5 bg-white/5 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-green-indie focus:ring-1 focus:ring-green-indie/50 placeholder-gray-600 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-1.5 text-xs font-semibold uppercase tracking-wider">
                        {t("modal.company")} <span className="text-gray-600 normal-case">{t("modal.companyOptional")}</span>
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Acme Inc."
                        className="w-full px-4 py-2.5 bg-white/5 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-green-indie focus:ring-1 focus:ring-green-indie/50 placeholder-gray-600 transition-all"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="mb-4">
                    <label className="block text-gray-400 mb-1.5 text-xs font-semibold uppercase tracking-wider">
                      {t("modal.email")}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      required
                      className="w-full px-4 py-2.5 bg-white/5 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-green-indie focus:ring-1 focus:ring-green-indie/50 placeholder-gray-600 transition-all"
                    />
                  </div>

                  {/* Project Type */}
                  <div className="mb-4">
                    <label className="block text-gray-400 mb-1.5 text-xs font-semibold uppercase tracking-wider">
                      {t("modal.projectType")}
                    </label>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 bg-white/5 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-green-indie focus:ring-1 focus:ring-green-indie/50 appearance-none transition-all"
                    >
                      <option value="" disabled className="bg-gray-900">
                        {t("modal.selectProject")}
                      </option>
                      <option value="Mobile Game" className="bg-gray-900">{t("modal.mobileGame")}</option>
                      <option value="Web Game" className="bg-gray-900">{t("modal.webGame")}</option>
                      <option value="Desktop Game" className="bg-gray-900">{t("modal.desktopGame")}</option>
                      <option value="Gamified App" className="bg-gray-900">{t("modal.gamifiedApp")}</option>
                      <option value="Interactive Experience" className="bg-gray-900">{t("modal.interactiveExp")}</option>
                      <option value="Prototype / MVP" className="bg-gray-900">{t("modal.prototypeMvp")}</option>
                      <option value="Other" className="bg-gray-900">{t("modal.other")}</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div className="mb-6">
                    <label className="block text-gray-400 mb-1.5 text-xs font-semibold uppercase tracking-wider">
                      {t("modal.tellUs")}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t("modal.messagePlaceholder")}
                      rows={4}
                      required
                      className="w-full px-4 py-2.5 bg-white/5 border border-gray-700 text-white rounded-lg focus:outline-none focus:border-green-indie focus:ring-1 focus:ring-green-indie/50 placeholder-gray-600 transition-all resize-none"
                    />
                  </div>

                  {/* Submit */}
                  {error && (
                    <p className="text-red-400 text-sm text-center mb-3">
                      Something went wrong. Please try again.
                    </p>
                  )}
                  <motion.button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-green-indie text-black font-bold py-3 rounded-lg hover:bg-green-600 transition-colors relative overflow-hidden group disabled:opacity-60 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="relative z-10">{sending ? "Sending..." : t("modal.send")}</span>
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                  </motion.button>
                </motion.form>
              ) : (
                <motion.div
                  key="success"
                  className="p-8 flex flex-col items-center justify-center min-h-[300px] text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", damping: 20, stiffness: 300 }}
                >
                  <motion.div
                    className="w-16 h-16 rounded-full bg-green-indie/20 flex items-center justify-center mb-6"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: "spring", stiffness: 400 }}
                  >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <motion.path
                        d="M5 13L9 17L19 7"
                        stroke="rgb(36, 233, 74)"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ delay: 0.3, duration: 0.4 }}
                      />
                    </svg>
                  </motion.div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {t("modal.successTitle")}
                  </h3>
                  <p className="text-gray-400 mb-8">
                    {t("modal.successMessage")}
                  </p>
                  <motion.button
                    onClick={handleClose}
                    className="bg-white/10 hover:bg-white/20 text-white font-semibold py-2.5 px-8 rounded-lg transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {t("modal.close")}
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
